import {
  KAART_STATUSSEN,
  aantalBezoeken,
  afkoppelStatus,
  maatregelenStatus,
  totaleBergingM3,
  type DashboardStats,
  type KaartStatus,
  type Registratie,
  type Woning,
} from '@/types/domain';
import { isHuurwoning } from '@/data/huurwoningen';

function legeTelling(): Record<KaartStatus, number> {
  return Object.fromEntries(KAART_STATUSSEN.map((status) => [status, 0])) as Record<
    KaartStatus,
    number
  >;
}

/**
 * De cijfers voor het dashboard.
 *
 * Twee reeksen die allebei optellen tot alle woningen in het plangebied: wat
 * er met het pand gebeurt, en wat er daarnaast op het perceel gebeurt.
 *
 * Ze worden geteld met dezelfde functies die de kaart kleuren, en over
 * dezelfde lijst woningen. Dat is met opzet: eerder telde het dashboard
 * anders dan de kaart, en dan kreeg je "bezocht 76" naast "niet bezocht 4"
 * op hetzelfde scherm. Wie het dashboard niet vertrouwt, vertrouwt de kaart
 * ook niet meer.
 *
 * Woningen zonder registratie tellen gewoon mee. Dat moet ook: een huurpand
 * waar nog niemand is geweest wordt door de corporatie afgekoppeld, en dat
 * hoort in het totaal te staan.
 */
export function berekenStats(
  woningen: Woning[],
  registraties: Registratie[]
): DashboardStats {
  const perBagId = new Map<string, Registratie>();
  for (const registratie of registraties) {
    if (registratie.bagId) perBagId.set(registratie.bagId, registratie);
  }

  /*
   * Dezelfde verzameling punten als op de kaart: elke woning uit het
   * plangebied, plus de handmatig geplaatste registraties, die geen BAG-id
   * hebben en dus niet in die lijst zitten.
   */
  const punten: { adres: Registratie['adres']; registratie: Registratie | undefined }[] = [
    ...woningen.map((woning) => ({
      adres: woning.adres,
      registratie: perBagId.get(woning.bagId),
    })),
    ...registraties
      .filter((registratie) => !registratie.bagId)
      .map((registratie) => ({ adres: registratie.adres, registratie })),
  ];

  const afkoppelen = legeTelling();
  const maatregelen = legeTelling();
  let berging = 0;

  for (const punt of punten) {
    const huur = isHuurwoning(punt.adres);
    const pand = afkoppelStatus(punt.registratie, huur);

    afkoppelen[pand] += 1;
    maatregelen[maatregelenStatus(punt.registratie)] += 1;

    // Alleen meetellen wat afgekoppeld is, vaststaat of overwogen wordt.
    if (
      punt.registratie &&
      (pand === 'Afgekoppeld' || pand === 'Wordt afgekoppeld' || pand === 'Overweegt afkoppelen')
    ) {
      berging += totaleBergingM3(punt.registratie);
    }
  }

  let subsidieIngediend = 0;
  let meerdereContacten = 0;
  let totaalBezoeken = 0;

  for (const registratie of registraties) {
    if (registratie.contactmomenten.length > 1) meerdereContacten += 1;
    totaalBezoeken += aantalBezoeken(registratie);

    if (
      registratie.subsidieStatus === 'Aanvraag ingediend' ||
      registratie.subsidieStatus === 'Toegekend'
    ) {
      subsidieIngediend += 1;
    }
  }

  return {
    totaalWoningen: punten.length,
    afkoppelen,
    maatregelen,
    subsidieIngediend,
    meerdereContacten,
    totaalBezoeken,
    totaleBergingM3: Math.round(berging * 10) / 10,
  };
}
